import { useEffect } from 'react'

import z from 'zod'

import { cn } from '@fortem-monorepo/js-utilities'
import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import {
  ConfirmationDialog,
  ConfirmationDialogProps,
  Form,
  FormDescription,
  FormError,
  FormField,
  FormItem,
  Label,
  NumberInput,
  Textarea,
} from '@fortem-monorepo/react-ui'

import { FormFieldRow } from '@/components/forms/BookTradeForm/FormFieldRow'
import { useAdjustPositionMutation } from '@/pages/pnl-attributions/mutations'
import { formatNumber } from '@/utilities/format'

export interface AdjustPositionDialogProps extends Partial<ConfirmationDialogProps> {
  mutationParams: {
    original_pnl: number
    fund_name: string
    value_date: string
    instrument_id: number
    unique_identifier: string
    batch_id?: number | null
  }
}

const adjustPositionSchema = z.object({
  target_net_pnl: z.coerce.number(),
  reason: z.string().min(1, 'You must provide a reason for adjusting the P&L'),
  ticket_reference: z.string().optional().nullable(),
})

export const AdjustPositionDialog: React.FC<AdjustPositionDialogProps> = ({ mutationParams, ...props }) => {
  const adjustPositionMutation = useAdjustPositionMutation({
    fund_name: mutationParams.fund_name,
    value_date: mutationParams.value_date,
  })

  const form = useFormWithSchema({ mode: 'all' }, adjustPositionSchema)

  useEffect(() => {
    form.trigger()
  }, [])

  const onSubmit = form.handleSubmit(async (data) => {
    await adjustPositionMutation.mutateAsync({
      ...data,
      fund_name: mutationParams.fund_name,
      value_date: mutationParams.value_date,
      instrument_id: mutationParams.instrument_id,
      batch_id: mutationParams.batch_id,
    })
    props.onClose?.()
  })

  const target_net_pnl = form.watch('target_net_pnl')
  const diff = target_net_pnl ? target_net_pnl - mutationParams.original_pnl : 0

  return (
    <ConfirmationDialog
      {...props}
      action={onSubmit}
      title={`Adjust position for ${mutationParams.unique_identifier} on ${mutationParams.value_date}`}
      description={
        'This will override the P&L of the entire position. The overriding P&L will be allocated pro-rata to each trade quantity.'
      }
      pending={adjustPositionMutation.isPending}
      valid={form.formState.isValid}
    >
      <div className="flex items-center gap-10">
        <div className="flex flex-col gap-1 pb-4">
          <span className="font-medium uppercase text-muted-foreground text-xs">Original P&L</span>
          <span className="font-mono text-sm">{formatNumber(mutationParams.original_pnl)}</span>
        </div>
        <div className="flex flex-col gap-1 pb-4">
          <span className="font-medium uppercase text-muted-foreground text-xs">Diff</span>
          <span className="font-mono text-sm">
            <div
              className={cn('flex items-center gap-px', {
                'text-emerald-700': diff > 0,
                'text-red-700': diff < 0,
              })}
            >
              {diff > 0 && <span>+</span>}
              <span className="font-mono text-sm">{formatNumber(diff)}</span>
            </div>
          </span>
        </div>
      </div>
      <Form {...form}>
        <FormFieldRow>
          <FormField
            name="target_net_pnl"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center gap-2">
                  <Label>New P&L</Label>
                  <FormError />
                </div>
                <FormDescription>The new P&L for the position.</FormDescription>
                <div className="flex items-center gap-2">
                  <NumberInput
                    thousandSeparator={','}
                    value={field.value}
                    onValueChange={(val) => field.onChange(val.floatValue)}
                  />
                </div>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="reason"
            render={({ field }) => {
              return (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <Label>Reason</Label>
                    <FormError />
                  </div>
                  <FormDescription>Provide a reason for the adjustment.</FormDescription>
                  <Textarea value={field.value} onChange={field.onChange} />
                </FormItem>
              )
            }}
          />
        </FormFieldRow>
      </Form>
    </ConfirmationDialog>
  )
}
